import React, { useEffect, useRef } from 'react';
import { isAndroid } from '../util/platform';
import { View, StyleSheet, Animated } from 'react-native'

import AppStyles from '../AppStyles';

/**
 * LoadingSkeleton - Animated loading placeholder component
 *
 * @param {number} width - Width of skeleton (use '100%' for full width or specific number)
 * @param {number} height - Height of skeleton in pixels
 * @param {number} borderRadius - Border radius (default: 8)
 * @param {object} style - Additional styles
 */
const LoadingSkeleton = ({ width = '100%', height = 20, borderRadius = 8, style }) => {
  const animatedValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(animatedValue, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(animatedValue, {
          toValue: 0,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    );

    animation.start();

    return () => animation.stop();
  }, [animatedValue]);

  const opacity = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  return (
    <Animated.View
      style={[
        styles.skeleton,
        {
          width,
          height,
          borderRadius,
          opacity,
        },
        style,
      ]}
    />
  );
};

/**
 * SkeletonCard - Card-shaped skeleton for loading states
 */
export const SkeletonCard = ({ children, style }) => {
  return (
    <View style={[styles.card, style]}>
      {children}
    </View>
  );
};

/**
 * SkeletonText - Text line skeleton
 */
export const SkeletonText = ({ width = '100%', style }) => {
  return <LoadingSkeleton width={width} height={16} borderRadius={4} style={style} />;
};

/**
 * SkeletonTitle - Title/heading skeleton
 */
export const SkeletonTitle = ({ width = '60%', style }) => {
  return <LoadingSkeleton width={width} height={24} borderRadius={6} style={style} />;
};

/**
 * SkeletonButton - Button skeleton
 */
export const SkeletonButton = ({ width = '100%', style }) => {
  return <LoadingSkeleton width={width} height={44} borderRadius={12} style={style} />;
};

/**
 * SkeletonAvatar - Circular avatar skeleton
 */
export const SkeletonAvatar = ({ size = 40, style }) => {
  return <LoadingSkeleton width={size} height={size} borderRadius={size / 2} style={style} />;
};

/**
 * ReportDetailsSkeleton - Full skeleton for AdminReportDetails screen
 */
export const ReportDetailsSkeleton = () => {
  return (
    <View style={styles.container}>
      {/* Report Header Skeleton */}
      <View style={{ marginBottom: 20 }}>
        <SkeletonTitle width="80%" />
        <SkeletonText width="40%" style={{ marginTop: 8 }} />
      </View>

      {/* Report Content Skeleton */}
      <View style={{ marginBottom: 24 }}>
        <SkeletonText width="95%" style={{ marginBottom: 8 }} />
        <SkeletonText width="90%" style={{ marginBottom: 8 }} />
        <SkeletonText width="85%" />
      </View>

      {/* Case Management Card Skeleton */}
      <SkeletonCard>
        <SkeletonTitle width="50%" style={{ marginBottom: 16 }} />

        {/* Status */}
        <SkeletonText width="30%" style={{ marginBottom: 8 }} />
        <SkeletonButton style={{ marginBottom: 20 }} />

        {/* Assignment */}
        <SkeletonText width="35%" style={{ marginBottom: 8 }} />
        <SkeletonButton style={{ marginBottom: 20 }} />

        {/* Divider */}
        <View style={styles.divider} />

        {/* Todos */}
        <SkeletonText width="25%" style={{ marginBottom: 12 }} />
        <View style={{ gap: 8 }}>
          <LoadingSkeleton height={60} borderRadius={12} />
          <LoadingSkeleton height={60} borderRadius={12} />
        </View>
      </SkeletonCard>

      {/* AI Classifications Card Skeleton */}
      <SkeletonCard style={{ marginTop: 16 }}>
        <SkeletonTitle width="45%" style={{ marginBottom: 16 }} />
        <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
          <LoadingSkeleton width={80} height={32} borderRadius={16} />
          <LoadingSkeleton width={100} height={32} borderRadius={16} />
          <LoadingSkeleton width={90} height={32} borderRadius={16} />
        </View>
      </SkeletonCard>

      {/* Follow Up Card Skeleton */}
      <SkeletonCard style={{ marginTop: 16 }}>
        <SkeletonTitle width="30%" style={{ marginBottom: 16 }} />
        <View style={{ gap: 12 }}>
          <LoadingSkeleton height={100} borderRadius={12} />
          <LoadingSkeleton height={100} borderRadius={12} />
        </View>
      </SkeletonCard>
    </View>
  );
};

const styles = StyleSheet.create({
  skeleton: {
    backgroundColor: '#E0E0E0', // More visible gray for loading skeleton
  },
  container: {
    padding: 20,
  },
  card: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: isAndroid() ? 0 : 2,
  },
  divider: {
    height: 1,
    backgroundColor: AppStyles.colorSet.cardBorder,
    marginVertical: 16,
  },
});

export default LoadingSkeleton;
